import React from "react";
import { Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { QUIZ } from "@/theme/quizTokens";

export type ChoiceOption = {
  value: string;
  label: string;
  sublabel?: string;
  /** Image URL for the tile icon (preferred — matches the PoP quiz icon set). */
  icon?: string;
  /** Emoji fallback when no icon image is available. */
  emoji?: string;
};

type Props = {
  options: ChoiceOption[];
  /** Selected value (single) or values (multiple). */
  selected: string | string[];
  onSelect: (value: string) => void;
  multiple?: boolean;
  /** Tile size — "md" for 2–3 options, "sm" to pack more per row. */
  size?: "sm" | "md";
  /**
   * Rendered full-width immediately after the selected tile (single-select
   * only) — used for inline reassurance so it appears next to the answer
   * instead of below the fold. In a wrapping flex row a 100%-width child
   * breaks onto its own line right after the tile, and later tiles reflow
   * after it.
   */
  afterSelected?: React.ReactNode;
};

/**
 * Shared selectable option used across the redesigned choice screens (sex, spayed,
 * body condition, activity, treat frequency, allergies). Renders the Figma icon-tile:
 * a cream tile (icon or emoji inside) with a bold caption + optional sub-label below.
 * Selected tiles get a brown border on white; multi-select adds a check badge.
 */
export function QuizChoiceCard({ options, selected, onSelect, multiple, size = "md", afterSelected }: Props) {
  const isSelected = (value: string) =>
    Array.isArray(selected) ? selected.includes(value) : selected === value;

  const tileW = size === "sm" ? { xs: 86, md: 104 } : { xs: 100, md: 130 };
  const tileH = size === "sm" ? { xs: 70, md: 86 } : { xs: 80, md: 104 };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: { xs: "8px", md: "14px" },
        justifyContent: "center",
        width: "100%",
      }}
    >
      {options.map((opt) => {
        const active = isSelected(opt.value);
        return (
          <React.Fragment key={opt.value}>
          <Box
            role="button"
            aria-pressed={active}
            onClick={() => onSelect(opt.value)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "9px",
              cursor: "pointer",
              userSelect: "none",
              width: tileW,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: tileH,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: "6px",
                boxSizing: "border-box",
                borderRadius: "16px",
                // Figma: idle tile = warm beige; selected tile = solid brown w/ cream icon
                bgcolor: active ? QUIZ.color.brown : "#e9e1d8",
                border: "1px solid",
                borderColor: active ? QUIZ.color.brown : "#ddd3c8",
                boxShadow: active ? "0px 3px 6px rgba(0,0,0,0.18)" : "none",
                transition: "all 0.18s ease",
                "&:hover": { borderColor: QUIZ.color.brown },
              }}
            >
              {opt.icon ? (
                <Box
                  component="img"
                  src={opt.icon}
                  alt=""
                  sx={{
                    maxWidth: "72%",
                    maxHeight: "72%",
                    objectFit: "contain",
                    display: "block",
                    // brown source SVGs → cream when the tile is selected
                    filter: active ? "brightness(0) saturate(100%) invert(97%) sepia(6%) saturate(682%) hue-rotate(324deg) brightness(103%) contrast(96%)" : "none",
                  }}
                />
              ) : opt.emoji ? (
                <Box sx={{ fontSize: { xs: 34, md: 42 }, lineHeight: 1 }}>{opt.emoji}</Box>
              ) : (
                <Typography
                  sx={{
                    fontFamily: QUIZ.font.body,
                    fontWeight: 700,
                    fontSize: { xs: 15, md: 17 },
                    color: active ? QUIZ.color.cream : QUIZ.color.ink,
                    textAlign: "center",
                    lineHeight: 1.1,
                  }}
                >
                  {opt.label}
                </Typography>
              )}

              {multiple && active && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    bgcolor: QUIZ.color.brown,
                    color: QUIZ.color.white,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckIcon sx={{ fontSize: 15 }} />
                </Box>
              )}
            </Box>

            {/* caption (only when the tile shows an icon/emoji) */}
            {(opt.icon || opt.emoji) && (
              <>
                <Typography
                  sx={{
                    fontFamily: QUIZ.font.body,
                    fontWeight: 700,
                    fontSize: { xs: 16, md: 17 },
                    color: QUIZ.color.ink,
                    textAlign: "center",
                    lineHeight: 1.1,
                  }}
                >
                  {opt.label}
                </Typography>
                {opt.sublabel && (
                  <Typography
                    sx={{
                      fontFamily: QUIZ.font.body,
                      fontSize: { xs: 14, md: 14 },
                      color: "#757575",
                      textAlign: "center",
                      lineHeight: 1.2,
                      mt: "-4px",
                    }}
                  >
                    {opt.sublabel}
                  </Typography>
                )}
              </>
            )}
          </Box>
          {active && afterSelected && (
            <Box sx={{ width: "100%" }}>{afterSelected}</Box>
          )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

export default QuizChoiceCard;
